import { logger } from "@/lib/logger";
// Performance monitoring utilities for production tracking

interface PerformanceMetric {
  name: string;
  duration: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private marks: Map<string, number> = new Map();

  // Start timing a specific operation
  startMark(name: string): void {
    this.marks.set(name, performance.now());
  }

  // End timing and record metric
  endMark(name: string, metadata?: Record<string, any>): number {
    const startTime = this.marks.get(name);
    if (!startTime) {
      logger.warn(`No start mark found for: ${name}`);
      return 0;
    }

    const duration = performance.now() - startTime;
    this.marks.delete(name);

    const metric: PerformanceMetric = {
      name,
      duration,
      timestamp: Date.now(),
      metadata,
    };

    this.metrics.push(metric);
    
    // Log slow operations in development
    if (import.meta.env.DEV && duration > 1000) {
      logger.warn(`Slow operation detected: ${name} took ${duration.toFixed(2)}ms`, metadata);
    }

    return duration;
  }

  // Get all recorded metrics
  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  // Get metrics for a specific operation
  getMetricsByName(name: string): PerformanceMetric[] {
    return this.metrics.filter((m) => m.name === name);
  }

  // Calculate average duration for an operation
  getAverageDuration(name: string): number {
    const metrics = this.getMetricsByName(name);
    if (metrics.length === 0) return 0;
    
    const total = metrics.reduce((sum, m) => sum + m.duration, 0);
    return total / metrics.length;
  }

  // Clear all metrics
  clear(): void {
    this.metrics = [];
    this.marks.clear();
  }

  // Get performance summary
  getSummary(): Record<string, { count: number; avg: number; max: number; min: number }> {
    const summary: Record<string, { count: number; avg: number; max: number; min: number }> = {};

    this.metrics.forEach((metric) => {
      if (!summary[metric.name]) {
        summary[metric.name] = {
          count: 0,
          avg: 0,
          max: 0,
          min: Infinity,
        };
      }

      const stats = summary[metric.name];
      stats.count++;
      stats.max = Math.max(stats.max, metric.duration);
      stats.min = Math.min(stats.min, metric.duration);
    });

    // Calculate averages
    Object.keys(summary).forEach((name) => {
      summary[name].avg = this.getAverageDuration(name);
    });

    return summary;
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Helper hook for React components
export function usePerformanceTracking(operationName: string) {
  return {
    start: () => performanceMonitor.startMark(operationName),
    end: (metadata?: Record<string, any>) => performanceMonitor.endMark(operationName, metadata),
  };
}

// NOTE: Web Vitals monitoring is handled by usePerformanceMonitor hook (src/hooks/usePerformanceMonitor.ts).
// Do NOT add duplicate PerformanceObserver instances here — multiple CLS/LCP/FID observers
// cause inflated metrics and unnecessary browser overhead.
